import { toTitleCase } from 'Helpers/StringUtils';
import { FileToBeUploaded, FileUpdates, UploadedFile } from 'Models/Files';
import { GroupOptionType, OptionType } from 'Models/Types/GlobalType';

import { OperationalControl } from './OperationalControls';
import { DueDiligenceModule, ProductModule } from './ProductModule';
import { DisplayableTag } from './Tags';

/**
 * Identifies the page a user navigated from when clicking "Create Issue" in `IssuesListing`.
 * Used to render an origin-aware breadcrumb on the Manage Issue page, so the user can navigate back to where they came from rather than seeing the default "Issues > Manage Issue" breadcrumb.
 */
export enum IssueBreadcrumbSource {
    ASSESSMENT = 'assessment',
    CONTROL_DETAILS = 'controlDetails',
    FINAL_REVIEW = 'finalReview',
    ISSUE_DETAILS = 'issueDetails',
    ISSUES_LISTING = 'issuesListing',
    ASSESSMENT_SUBJECT_DASHBOARD = 'assessmentSubjectDashboard',
}

/**
 * The context needed to reconstruct the breadcrumb trail of the page a "Create Issue" action originated from.
 * The IDs and human-readable display names are carried in the create-issue URL's search params (see `getCreateIssueUrl`).
 */
export type ManageIssueOrigin =
    | {
          source: IssueBreadcrumbSource.ASSESSMENT;
          assessmentId: string;
          assessmentName: string;
          module: DueDiligenceModule;
          assessmentSubjectDisplayName: string;
          assessmentSubjectId: string;
          organizationId: string;
      }
    | {
          source: IssueBreadcrumbSource.CONTROL_DETAILS;
          controlDisplayName: string;
          controlGroupName: string;
          controlIdentifier: string;
      }
    | {
          source: IssueBreadcrumbSource.FINAL_REVIEW;
          module: DueDiligenceModule;
          assessmentSubjectDisplayName: string;
          assessmentSubjectId: string;
          organizationId: string;
      }
    | {
          source: IssueBreadcrumbSource.ISSUE_DETAILS;
          module: ProductModule;
      }
    | {
          source: IssueBreadcrumbSource.ISSUES_LISTING;
          module: ProductModule;
      }
    | {
          source: IssueBreadcrumbSource.ASSESSMENT_SUBJECT_DASHBOARD;
          module: DueDiligenceModule;
          assessmentSubjectDisplayName: string;
          assessmentSubjectId: string;
          organizationId: string;
      };

/**
 * Create and update requests write this to the database. This is not sent in responses. Instead, it is converted to a DTO, since the front-end needs to know:
 * 1. Whether the issue belongs to the Operational Controls module or the TPRM module.
 * 2. Additional details about the controls that an issue is mapped to, if it belongs in the Operational Controls module.
 */
export interface Issue {
    id: string;
    created_by: string;
    created_timestamp: string;
    description: string;
    last_updated_by: string;
    last_updated_timestamp: string;
    status: IssueStatus;
    title: string;
    type: ProductModule;

    delegates: string[];
    due_date?: string;
    files: UploadedFile[];
    impacted_controls: string[];
    impacted_vendor?: string;
    owner_subject?: string;
    priority?: IssuePriority;
    reference?: string;
    remediation_plan?: string;
    risk_assessment?: string;
    source?: string;
    tags: string[];

    // These fields are always present when the issue is CLOSED.
    closure_statement?: string;
    closed_timestamp?: string;
    closed_by?: string;
}

/** A request to create a new Issue. */
export interface CreateIssueRequest {
    status: IssueStatus.DRAFT_OPEN | IssueStatus.OPEN;
    title: string;
    description: string;
    type: ProductModule;
    owner_subject?: string;
    due_date?: string;
    source?: string;
    priority?: IssuePriority;
    remediation_plan?: string;
    risk_assessment?: string;

    delegates: string[];
    reference?: string;
    impacted_controls: string[];
    impacted_vendor?: string;
    files: FileToBeUploaded[];
    tags: string[];
}

/** A request to update an existing Issue. */
export interface UpdateIssueRequest {
    status: IssueStatus;
    title: string;
    description: string;
    type: ProductModule;
    owner_subject?: string;
    due_date?: string;
    priority?: IssuePriority;
    source?: string;
    remediation_plan?: string;
    risk_assessment?: string;

    delegates: string[];
    reference?: string;
    impacted_controls: string[];
    impacted_vendor?: string;
    file_updates: FileUpdates;
    tags: string[];

    closure_statement?: string;
}

/** A DTO used to make Issue presentable to front-end clients. `impacted_controls` is always enriched with full Control details rather than just Control IDs (empty when the Issue isn't mapped to Controls). */
export type IssueResponse = Omit<Issue, 'impacted_controls'> & { impacted_controls: OperationalControl[] };

/** This object defines changes to an Issue's attributes that are necessary when the Issue is saved as a history record. */
export type IssueHistoryModifications = Omit<Issue, 'tags'> & { tags: DisplayableTag[] };

/** A DTO used to make an IssueHistoryModifications presentable to front-end clients. See `IssueResponse` for the rationale behind the `impacted_controls` override. */
export type IssueHistoryModificationsResponse = Omit<IssueHistoryModifications, 'impacted_controls'> & { impacted_controls: OperationalControl[] };

/** A DTO used to make IssueHistory presentable to front-end clients. */
export interface IssueHistoryResponse {
    timestamp: string;
    issue: IssueHistoryModificationsResponse;
}

/**
 * A DTO used to make an Issue presentable to front-end clients for the Export feature.
 * The difference is the Export version of an Issue needs UI-friendly versions of Controls or Third-Party names, depending on which feature the Issue belongs to.
 */
export interface IssueExportResponse extends Omit<Issue, 'impacted_controls' | 'impacted_vendor'> {
    impacted_controls: OperationalControl[];
    impacted_vendor?: string; // Technically this is the same data type as defined in the Issue interface, but it's listed here in order to highlight that this is the Third-Party's name instead of their UUID.
}

export enum IssuesSortFilterOptions {
    STATUS = 'status',
    OWNER = 'owner_subject',
    TITLE = 'title',
    DUE_DATE = 'due_date',
    DESCRIPTION = 'description',
    PRIORITY = 'priority',
    SOURCE = 'source',
    CONTROLS = 'controls',
    THIRD_PARTY = 'impacted_vendor',
    TAGS = 'tags',
}

export enum IssuePriority {
    LOW = 'LOW',
    MEDIUM = 'MEDIUM',
    HIGH = 'HIGH',
}

const priorityNumberValue = (priority: IssuePriority) => {
    switch (priority) {
        case IssuePriority.LOW:
            return 0;
        case IssuePriority.MEDIUM:
            return 1;
        case IssuePriority.HIGH:
            return 2;
    }
};

export const priorityComparator = (priorityA: IssuePriority, priorityB: IssuePriority): number => {
    if (priorityA === priorityB) {
        return 0;
    } else {
        const valueA = priorityNumberValue(priorityA);
        const valueB = priorityNumberValue(priorityB);
        return valueA < valueB ? 1 : -1;
    }
};

export const IssuePriorityOptions: OptionType[] = [
    {
        value: IssuePriority.LOW,
        label: toTitleCase(IssuePriority.LOW),
    },
    {
        value: IssuePriority.MEDIUM,
        label: toTitleCase(IssuePriority.MEDIUM),
    },
    {
        value: IssuePriority.HIGH,
        label: toTitleCase(IssuePriority.HIGH),
    },
];

export const IssuePriorityFilterOptionTypes: GroupOptionType[] = [
    {
        groupId: IssuesSortFilterOptions.PRIORITY,
        label: 'Low',
        value: IssuePriority.LOW,
    },
    {
        groupId: IssuesSortFilterOptions.PRIORITY,
        label: 'Medium',
        value: IssuePriority.MEDIUM,
    },
    {
        groupId: IssuesSortFilterOptions.PRIORITY,
        label: 'High',
        value: IssuePriority.HIGH,
    },
];

export enum IssuePriorityFilter {
    ALL = 'ALL',
    LOW = 'LOW',
    MEDIUM = 'MEDIUM',
    HIGH = 'HIGH',
}

export const isValidIssuePriorityFilter = (value: unknown): value is IssuePriorityFilter => typeof value === 'string' && Object.values(IssuePriorityFilter).includes(value as IssuePriorityFilter);

export const IssuePriorityFilterOptions: OptionType[] = [
    {
        value: IssuePriorityFilter.ALL,
        label: 'All Priorities',
    },
    {
        value: IssuePriorityFilter.LOW,
        label: toTitleCase(IssuePriorityFilter.LOW),
    },
    {
        value: IssuePriorityFilter.MEDIUM,
        label: toTitleCase(IssuePriorityFilter.MEDIUM),
    },
    {
        value: IssuePriorityFilter.HIGH,
        label: toTitleCase(IssuePriorityFilter.HIGH),
    },
];

export enum IssueStatus {
    DRAFT_OPEN = 'DRAFT_OPEN',
    OPEN = 'OPEN',
    CLOSED = 'CLOSED',
}

export const IssueStatusFilterOptionTypes: GroupOptionType[] = [
    {
        groupId: IssuesSortFilterOptions.STATUS,
        label: 'Open',
        value: IssueStatus.OPEN,
    },
    {
        groupId: IssuesSortFilterOptions.STATUS,
        label: 'Draft Open',
        value: IssueStatus.DRAFT_OPEN,
    },
    {
        groupId: IssuesSortFilterOptions.STATUS,
        label: 'Closed',
        value: IssueStatus.CLOSED,
    },
];

export const statusComparator = (statusA: IssueStatus, statusB: IssueStatus): number => {
    const statusValue = (status: IssueStatus): number => {
        switch (status) {
            case IssueStatus.DRAFT_OPEN:
                return 0;
            case IssueStatus.OPEN:
                return 1;
            case IssueStatus.CLOSED:
                return 2;
        }
    };

    const statusAValue = statusValue(statusA);
    const statusBValue = statusValue(statusB);
    return statusAValue - statusBValue;
};

/**
 * This relatively simple function exists because we have previously had inconsistencies in whether drafts are considered "open".
 * @returns The number of issues in the provided list that are considered "open".
 */
export const countOpenIssues = (issues: IssueResponse[]): number => issues.filter((issue) => issue.status === IssueStatus.OPEN).length;
